import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Users, Plus, Trash2 } from 'lucide-react';
import { ticketingApi, type TeamMember } from '@/lib/ticketing-api';
import { PageHeader, DataTable, type Column } from '@/components/ui/DataTable';
import { StatusPill, KpiStat } from '@/components/ui/primitives';
import { QuickFormModal } from '@/components/QuickFormModal';
import { ConfirmDialog } from '@/components/ui/dialogs';
import { timeAgo } from '@/i18n/format';

const ROLES = ['tenant_admin', 'operator', 'technician', 'viewer'] as const;

export function TeamPage() {
  const { t } = useTranslation('pages');
  const ROLE_LABEL: Record<string, string> = {
    tenant_admin: t('team.roles.tenant_admin'),
    operator: t('team.roles.operator'),
    technician: t('team.roles.technician'),
    viewer: t('team.roles.viewer'),
    super_admin: t('team.roles.super_admin'),
  };
  const queryClient = useQueryClient();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invited, setInvited] = useState<{ email: string; emailSent?: boolean } | null>(null);
  const [removeFor, setRemoveFor] = useState<TeamMember | null>(null);
  const [removing, setRemoving] = useState(false);
  const [roleBusy, setRoleBusy] = useState<string | null>(null); // member id mid-update
  const { data: team, isLoading } = useQuery({ queryKey: ['team'], queryFn: ticketingApi.listTeam });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['team'] });

  // Short, plain descriptions of what each role can do (shown in the invite form
  // and as a tooltip on the inline role select) — operators shouldn't have to
  // guess what "technician" vs "operator" grants.
  const ROLE_DESC: Record<string, string> = {
    tenant_admin: t('team.roleDesc.tenant_admin'),
    operator: t('team.roleDesc.operator'),
    technician: t('team.roleDesc.technician'),
    viewer: t('team.roleDesc.viewer'),
  };

  const active = (team ?? []).filter((m) => m.status === 'active').length;

  const columns: Column<TeamMember>[] = [
    {
      key: 'name', header: t('team.columns.member'),
      render: (m) => (
        <div className="flex items-center gap-3">
          <span className="grid h-8 w-8 place-items-center rounded-full bg-gradient-to-br from-primary to-accent/70 text-xs font-semibold text-white">{m.name.slice(0, 1)}</span>
          <div><div className="font-medium">{m.name}</div><div className="text-xs text-muted">{m.email}</div></div>
        </div>
      ),
    },
    // Inline role change — writes the Logto org-role. Disabled while saving to
    // avoid a double-submit; the role's description is the select's tooltip.
    { key: 'role', header: t('team.columns.role'), render: (m) => {
      const value = ROLES.includes(m.role as typeof ROLES[number]) ? m.role : 'viewer';
      return (
        <select
          className="tk-input py-0.5 text-xs"
          value={value}
          disabled={roleBusy === m.id}
          title={ROLE_DESC[value]}
          onChange={async (e) => {
            setRoleBusy(m.id);
            try { await ticketingApi.setTeamRole(m.id, e.target.value); refresh(); }
            finally { setRoleBusy(null); }
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
        </select>
      );
    } },
    { key: 'status', header: t('team.columns.status'), render: (m) => <StatusPill status={m.status === 'active' ? 'active' : 'offline'} label={m.status === 'active' ? t('team.status.active') : t('team.status.inactive')} /> },
    { key: 'lastActive', header: t('team.columns.lastActive'), align: 'right', render: (m) => <span className="tk-num text-muted" title={new Date(m.lastActive).toLocaleString()}>{timeAgo(m.lastActive)}</span> },
    { key: 'remove', header: '', render: (m) => (
      <button
        className="tk-btn-ghost px-1.5 py-1" title={t('team.actions.remove')}
        onClick={(e) => { e.stopPropagation(); setRemoveFor(m); }}
      ><Trash2 size={14} /></button>
    ) },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title={t('team.title')} subtitle={t('team.subtitle')}
        action={<button className="tk-btn-primary" onClick={() => setInviteOpen(true)}><Plus size={16} /> {t('team.actions.invite')}</button>} />

      {inviteOpen && (
        <QuickFormModal
          title={t('team.invite.title')}
          submitLabel={t('team.invite.submit')}
          fields={[
            { key: 'name', label: t('team.invite.fields.name'), required: true, placeholder: t('team.invite.placeholders.name') },
            { key: 'email', label: t('team.invite.fields.email'), type: 'email', required: true, placeholder: t('team.invite.placeholders.email') },
            { key: 'phone', label: t('team.invite.fields.phone', { defaultValue: 'Phone (for SMS verification)' }), placeholder: '+972...' },
            { key: 'role', label: t('team.invite.fields.role'), type: 'select', defaultValue: 'operator', options: [
              { value: 'tenant_admin', label: `${t('team.roles.tenant_admin')} — ${ROLE_DESC.tenant_admin}` },
              { value: 'operator', label: `${t('team.roles.operator')} — ${ROLE_DESC.operator}` },
              { value: 'technician', label: `${t('team.roles.technician')} — ${ROLE_DESC.technician}` },
              { value: 'viewer', label: `${t('team.roles.viewer')} — ${ROLE_DESC.viewer}` },
            ] },
          ]}
          onClose={() => setInviteOpen(false)}
          onSubmit={async (v) => {
            const r = await ticketingApi.inviteTeamMember({ name: v.name, email: v.email, role: v.role || 'viewer', phone: v.phone || undefined });
            refresh();
            setInvited({ email: r?.email ?? v.email, emailSent: r?.emailSent });
          }}
        />
      )}

      {invited && (
        <div className="tk-modal-overlay" onClick={() => setInvited(null)}>
          <div className="tk-modal w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 text-base font-semibold">{t('team.invited.title')}</div>
            <p className="text-sm text-muted">{t('team.invited.added', { email: invited.email })}</p>
            <div className="mt-3 rounded-md bg-ok/10 p-3 text-sm ring-1 ring-ok/30 text-muted">
              {invited.emailSent === false
                ? t('team.invited.emailFailed', { defaultValue: "We couldn't send the invite email automatically. Ask the user to check spam, or resend the invitation." })
                : t('team.invited.emailSent', { defaultValue: 'An invitation email was sent with a secure link to set their password. The link expires in 3 days.' })}
            </div>
            <button className="tk-btn-primary mt-4 w-full justify-center" onClick={() => setInvited(null)}>{t('team.invited.done')}</button>
          </div>
        </div>
      )}

      {removeFor && (
        <ConfirmDialog
          title={t('team.confirmRemoveTitle')}
          message={t('team.confirmRemove', { name: removeFor.name })}
          confirmLabel={t('team.actions.remove')}
          busy={removing}
          onConfirm={async () => {
            setRemoving(true);
            try { await ticketingApi.removeTeamMember(removeFor.id); refresh(); setRemoveFor(null); }
            finally { setRemoving(false); }
          }}
          onClose={() => setRemoveFor(null)}
        />
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiStat label={t('team.kpi.members')} value={team?.length ?? 0} tone="info" />
        <KpiStat label={t('team.kpi.active')} value={active} tone="ok" />
        <KpiStat label={t('team.kpi.admins')} value={(team ?? []).filter((m) => m.role.includes('admin')).length} tone="info" />
        <KpiStat label={t('team.kpi.viewers')} value={(team ?? []).filter((m) => m.role === 'viewer').length} tone="muted" />
      </div>
      <DataTable columns={columns} rows={team} loading={isLoading} emptyIcon={Users} emptyTitle={t('team.empty.title')} emptyHint={t('team.empty.hint')} />
    </div>
  );
}
